import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { UpdateTerminalRequest } from '@models/terminal/terminal.model';
import { ConnectionStatusService } from '@services/connection-status.service';
import { TerminalService } from '@services/terminal.service';

@Component({
  selector: 'app-terminal-order-modes',
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './terminal-order-modes.component.html',
})
export class TerminalOrderModesComponent {
  private readonly terminalService = inject(TerminalService);
  private readonly connectionStatusService = inject(ConnectionStatusService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  private readonly terminal = this.terminalService.currentTerminal;

  protected readonly isOffline = computed(
    () => this.connectionStatusService.status() === 'common.offline',
  );
  protected readonly mealPlanEnabled = computed(
    () => this.terminal()?.is_meal_plan_enabled !== false,
  );
  protected readonly byCourseEnabled = computed(
    () => this.terminal()?.is_by_course_enabled !== false,
  );

  toggleMealPlan(): void {
    this.#update(!this.mealPlanEnabled(), this.byCourseEnabled());
  }

  toggleByCourse(): void {
    this.#update(this.mealPlanEnabled(), !this.byCourseEnabled());
  }

  #update(isMealPlanEnabled: boolean, isByCourseEnabled: boolean): void {
    const terminal = this.terminal();
    if (!terminal) {
      return;
    }

    if (!isMealPlanEnabled && !isByCourseEnabled) {
      this.toastr.error(
        this.translate.instant('admin_settings.order_modes.errors.at_least_one_message'),
        this.translate.instant('admin_settings.order_modes.errors.at_least_one_title'),
      );
      return;
    }

    const request: UpdateTerminalRequest = {
      ...terminal,
      is_meal_plan_enabled: isMealPlanEnabled,
      is_by_course_enabled: isByCourseEnabled,
    };

    this.terminalService.updateTerminal(request).subscribe({
      error: () => {
        this.toastr.error(
          this.translate.instant('admin_settings.order_modes.errors.update_error_message'),
          this.translate.instant('admin_settings.order_modes.errors.update_error_title'),
        );
      },
    });
  }
}
